import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  // Base styles - Institutional Editor
  [
    'inline-flex items-center justify-center gap-2.5',
    'font-medium text-sm',
    'rounded-[12px]', // xl radius from design system
    'transition-all duration-300',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.98]',
    'min-h-[44px]', // Touch-friendly
  ],
  {
    variants: {
      variant: {
        // Primary: Gold gradient - Institutional Editor signature (Prestige Gold)
        primary: [
          'bg-gradient-to-r from-[#f2ca50] to-[#d4af37]',
          'text-[#3c2f00]', // on_primary from design system
          'font-semibold',
          'hover:from-[#f5d65a] hover:to-[#dabb42]',
          'hover:-translate-y-0.5',
          'shadow-[0_4px_12px_rgba(242,202,80,0.25)]',
          'hover:shadow-[0_6px_20px_rgba(242,202,80,0.35)]',
        ],
        // Secondary: Surface container colors - No border
        secondary: [
          'bg-surface-container text-on-surface',
          'hover:bg-surface-container-highest',
        ],
        // Ghost: Minimal
        ghost: [
          'bg-transparent text-text-secondary',
          'hover:bg-surface-container-low hover:text-text-primary',
        ],
        // Success: Muted green gradient (dark text for contrast on light green)
        success: [
          'bg-gradient-to-r from-[#34d399] to-[#22c55e]',
          'text-[#3c2f00]',
          'font-semibold',
          'hover:from-[#4ade80] hover:to-[#34d399]',
          'shadow-[0_2px_8px_rgba(34,197,94,0.25)]',
          'hover:shadow-[0_4px_16px_rgba(34,197,94,0.35)]',
        ],
        // Danger: Muted red gradient
        danger: [
          'bg-gradient-to-r from-[#f87171] to-[#ef4444]',
          'text-white',
          'font-semibold',
          'hover:from-[#fca5a5] hover:to-[#f87171]',
          'shadow-[0_2px_8px_rgba(239,68,68,0.25)]',
          'hover:shadow-[0_4px_16px_rgba(239,68,68,0.35)]',
        ],
        // Outline: Ghost Border (outline_variant at 20%)
        outline: [
          'bg-transparent text-on-surface',
          'border border-border-subtle',
          'hover:bg-surface-container-low hover:border-border-subtle',
        ],
        // Warning: Warm amber
        warning: [
          'bg-gradient-to-r from-[#fbbf24] to-[#f59e0b]',
          'text-[#3c2f00]',
          'font-semibold',
          'hover:from-[#fcd34d] hover:to-[#fbbf24]',
        ],
        // Link: Subtle - Primary color
        link: [
          'bg-transparent text-primary',
          'hover:text-primary-container hover:underline',
          'p-0 h-auto min-h-0',
        ],
      },
      size: {
        sm: 'px-3 py-1.5 text-xs min-h-[36px]',
        md: 'px-5 py-2.5 text-sm',
        lg: 'px-6 py-3 text-base min-h-[48px]',
        icon: 'p-2.5 min-h-[44px] min-w-[44px]',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <LoadingSpinner />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

// Loading spinner component
const LoadingSpinner: React.FC = () => (
  <svg
    className="h-4 w-4 animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export { Button, buttonVariants };
